import { ComponentRef, InjectionToken, Injectable, Injector, NgZone, StaticProvider, Type, inject } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** Chrome context handed to any rate-sheet rail picker the popover hosts. */
export interface RsPopoverContext {
  title: string;
  close: () => void;
}
export const RS_POPOVER_CONTEXT = new InjectionToken<RsPopoverContext>('RsPopoverContext');

interface OpenOptions {
  anchorEl: HTMLElement;
  title: string;
  width?: number;
  onClose?: () => void;
  providers?: StaticProvider[];
}

/**
 * RsPopoverService — opens a rail picker component in a CDK overlay anchored
 * to the trigger element, with Esc + outside-click close. Reusable across the
 * MYGA / Standard Fixed / Index / RILA sheets.
 */
@Injectable({ providedIn: 'root' })
export class RsPopoverService {
  private overlay = inject(Overlay);
  private parentInjector = inject(Injector);
  private zone = inject(NgZone);

  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<unknown> | null = null;
  private anchorEl: HTMLElement | null = null;
  private currentOnClose: (() => void) | null = null;
  private outsideClick$ = new Subject<void>();

  isOpenFor(anchorEl: HTMLElement): boolean {
    return this.overlayRef != null && this.anchorEl === anchorEl;
  }

  open<T>(component: Type<T>, opts: OpenOptions): ComponentRef<T> | null {
    if (this.overlayRef) {
      const sameAnchor = this.anchorEl === opts.anchorEl;
      this.close();
      if (sameAnchor) return null;
    }

    const positions: ConnectedPosition[] = [
      { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
      { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8 },
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    ];

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(opts.anchorEl)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);

    const overlayRef = this.overlay.create(new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'rs-popover-panel',
    }));
    this.overlayRef = overlayRef;
    this.anchorEl = opts.anchorEl;
    this.currentOnClose = opts.onClose ?? null;

    const ctx: RsPopoverContext = { title: opts.title, close: () => this.close() };
    const injector = Injector.create({
      parent: this.parentInjector,
      providers: [{ provide: RS_POPOVER_CONTEXT, useValue: ctx }, ...(opts.providers ?? [])],
    });

    const ref = overlayRef.attach(new ComponentPortal(component, null, injector));
    this.componentRef = ref;

    overlayRef.keydownEvents().subscribe((e) => { if (e.key === 'Escape') this.close(); });

    this.outsideClick$ = new Subject<void>();
    this.zone.runOutsideAngular(() => {
      fromEvent<PointerEvent>(document, 'pointerdown', { capture: true })
        .pipe(takeUntil(this.outsideClick$))
        .subscribe((ev) => {
          const target = ev.target as HTMLElement | null;
          if (!target) return;
          if (this.overlayRef?.overlayElement.contains(target)) return;
          if (this.anchorEl?.contains(target)) return;
          this.zone.run(() => this.close());
        });
    });

    return ref;
  }

  close(): void {
    if (!this.overlayRef) return;
    this.outsideClick$.next();
    this.outsideClick$.complete();
    const onClose = this.currentOnClose;
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.componentRef = null;
    this.anchorEl = null;
    this.currentOnClose = null;
    onClose?.();
  }
}
